import { X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useToast } from "~/components/ui/use-toast";
import { cn } from "~/lib/utils";
import {
  PRIVACY_UPDATED_AT,
  PrivacyContent,
} from "~/modules/Landing/components/legal/PrivacyContent";
import { TERMS_UPDATED_AT, TermsContent } from "~/modules/Landing/components/legal/TermsContent";

import type { ReactNode } from "react";

interface TermsCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  /** Marca visual de validación: el submit se intentó sin aceptar. */
  error?: boolean;
  /**
   * Cuántas veces se intentó enviar. Sube con cada intento fallido, incluso cuando
   * `error` ya venía en `true`.
   *
   * Sin esto el aviso salía UNA sola vez: el efecto se dispara cuando `error` CAMBIA, y
   * en el segundo intento ya valía `true`, así que React no lo volvía a correr. El
   * alumno tocaba de nuevo, el toast ya se había ido solo, y otra vez sentía que el
   * botón no hacía nada — justo lo que se venía a arreglar.
   */
  errorNonce?: number;
}

/**
 * Aceptación de Términos y Política, con su propia validación visual.
 *
 * El problema que resuelve: el alumno completaba nombre, apellido y teléfono, tocaba
 * "Enviar código" y no pasaba nada evidente. El error existía —un renglón rojo debajo—
 * pero se lo perdía: en un teléfono, con el teclado abierto, el checkbox queda fuera de
 * la pantalla, así que el mensaje aparecía en una parte del formulario que no estaba
 * mirando. Y lo único que cambiaba de color era el borde del cuadradito de 16px.
 *
 * Tres cosas lo arreglan, y las tres viven ACÁ y no en cada formulario, así el registro
 * y la inscripción a un curso se comportan igual sin duplicar nada:
 *
 * 1. Al pasar a error, el componente se trae la pantalla y toma el foco. Es lo que
 *    convierte un mensaje que estaba abajo en algo imposible de no ver.
 * 2. El texto entero se pone rojo, no sólo el borde del cuadrito.
 * 3. El aviso visible es un toast; el texto atado al control por `aria-describedby`
 *    queda en `sr-only`, para que un lector de pantalla lo lea como el error de ESTE
 *    campo aunque en la pantalla no haya ningún renglón.
 *
 * Además del mensaje en su lugar, sale un toast. Es el aviso que se ve aunque el ojo
 * esté en otra parte del formulario, y usa el mismo `useToast` que ya avisa los errores
 * de OTP y de inscripción — no se inventó un sistema nuevo.
 *
 * El renglón rojo que estaba debajo de la casilla se sacó por pedido de Rodrigo: con el
 * toast, repetir el mismo texto ahí abajo era ruido. Lo que SÍ queda es la señal en el
 * propio campo —casilla con borde rojo y etiqueta en rojo—, que es la que sobrevive
 * cuando el toast se va solo.
 *
 * Los enlaces a los textos legales quedaron FUERA del <label>: un <button> adentro de un
 * <label> es HTML inválido y hacía que abrir los Términos también marcara la casilla
 * (hoy lo tapa un preventDefault, que es un parche sobre un marcado que no cierra).
 */
export function TermsCheckbox({
  checked,
  onCheckedChange,
  id = "terms-checkbox",
  error = false,
  errorNonce = 0,
}: TermsCheckboxProps) {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const errorId = useId();
  const boxRef = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();

  // Traer el campo a la vista y darle el foco cuando aparece el error. Sin esto el
  // mensaje puede quedar fuera de pantalla y el formulario parece que no responde.
  useEffect(() => {
    if (!error) return;
    const el = boxRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // `preventScroll` porque el scroll suave ya está en curso: sin esto el foco pega un
    // salto instantáneo y se pierde la animación.
    el.focus({ preventScroll: true });
    toast({
      variant: "destructive",
      description: "Aceptá los Términos y Condiciones para completar tu registro.",
    });
  }, [error, errorNonce, toast]);

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start gap-2">
          {/* `relative` + `before:` agranda SOLO el área táctil hasta 44px, sin mover ni
              agrandar el cuadrito dibujado: en un teléfono, 16px es la mitad del mínimo. */}
          <Checkbox
            ref={boxRef}
            id={id}
            checked={checked}
            onCheckedChange={(val) => onCheckedChange(val === true)}
            aria-required="true"
            aria-invalid={error || undefined}
            aria-describedby={error ? errorId : undefined}
            className={cn(
              "relative mt-0.5 before:absolute before:-inset-3.5 before:content-['']",
              error && "border-landing-red ring-2 ring-landing-red/30",
            )}
          />
          <label
            htmlFor={id}
            className={cn(
              "cursor-pointer text-sm leading-snug",
              error ? "font-medium text-landing-red" : "text-neutral-700",
            )}
          >
            Acepto los Términos y Condiciones y la Política de Privacidad
          </label>
        </div>

        {/* Los enlaces van aparte del label: adentro, tocarlos marcaría la casilla. */}
        <p className="pl-6 text-[13px] leading-snug text-neutral-600">
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="font-semibold underline underline-offset-2 hover:text-landing-red"
          >
            Leer los Términos
          </button>
          <span aria-hidden> · </span>
          <button
            type="button"
            onClick={() => setPrivacyOpen(true)}
            className="font-semibold underline underline-offset-2 hover:text-landing-red"
          >
            Leer la Política de Privacidad
          </button>
        </p>

        {/* El aviso visible es el toast; acá el mismo texto queda SÓLO para lectores de
            pantalla (`sr-only`). No es redundancia: `aria-describedby` tiene que apuntar
            a un elemento que exista —si no, la referencia queda rota y el campo se queda
            sin descripción de error—, y un toast que se va solo a los pocos segundos no
            sirve como descripción permanente del control. Para quien ve la pantalla no
            cambia nada: el texto no ocupa lugar. */}
        {error && (
          <p id={errorId} role="alert" className="sr-only">
            Tenés que aceptar los Términos y Condiciones para continuar.
          </p>
        )}
      </div>

      <LegalDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        title="Términos y Condiciones"
        updatedAt={TERMS_UPDATED_AT}
        href="/terms"
      >
        <TermsContent inDialog />
      </LegalDialog>

      <LegalDialog
        open={privacyOpen}
        onOpenChange={setPrivacyOpen}
        title="Política de privacidad"
        updatedAt={PRIVACY_UPDATED_AT}
        href="/privacy"
      >
        <PrivacyContent inDialog />
      </LegalDialog>
    </>
  );
}

/**
 * Diálogo con el texto legal COMPLETO — el mismo componente que renderizan /terms y /privacy.
 * Antes mostraba un placeholder ("Este contenido se actualizará próximamente…"): el alumno
 * aceptaba un texto vacío.
 *
 * Va sobre el papel de la marca (`bg-landing-paper`, texto `landing-text`): es la misma hoja
 * que se ve en /terms, y como el texto legal usa tokens `landing-*`, fondo y tinta cambian
 * JUNTOS en modo oscuro (los dos son variables con su variante oscura en index.css). Con el
 * `bg-background` por defecto del diálogo, fondo y texto responderían a escalas distintas.
 */
function LegalDialog({
  open,
  onOpenChange,
  title,
  updatedAt,
  href,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  updatedAt: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Ancho en mobile: el diálogo base es una grilla, y una columna implícita crece hasta el
          ancho mínimo de su contenido. Con la tabla de cookies adentro (520px) la columna medía
          más que la pantalla y TODO el diálogo scrolleaba de costado: el texto quedaba cortado y
          la X de cerrar, afuera. `minmax(0,1fr)` ata la columna al ancho del diálogo, y el
          `overflow-x-hidden` asegura que nada vuelva a abrir un scroll horizontal. */}
      <DialogContent
        noCloseButton
        className="max-h-[85dvh] grid-cols-[minmax(0,1fr)] overflow-x-hidden border-landing-border bg-landing-paper p-5 text-landing-text sm:max-w-2xl sm:p-6"
      >
        <DialogHeader className="text-left">
          {/* La X va en la MISMA fila que el título (y no con el `absolute top-4` del diálogo
              base, que la dejaba 8px más arriba que el texto). */}
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="min-w-0 font-display text-[20px] font-bold leading-tight text-landing-text sm:text-[22px]">
              {title}
            </DialogTitle>
            <DialogClose
              aria-label="Cerrar"
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-landing-muted transition-colors hover:bg-landing-cream hover:text-landing-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red/40"
            >
              <X className="size-5" aria-hidden="true" />
            </DialogClose>
          </div>
          <DialogDescription className="text-[12.5px] text-landing-muted">
            Última actualización: {updatedAt} ·{" "}
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-landing-red underline underline-offset-2"
            >
              Abrir en otra pestaña
            </a>
          </DialogDescription>
        </DialogHeader>
        {/* `overflow-wrap:anywhere`: un email o una URL larga corta línea en vez de empujar. */}
        <div className="min-w-0 border-t border-landing-border pt-5 [overflow-wrap:anywhere]">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
